import { useClientPortalAPI, useSubmit } from "@certn/api";
import {
  Layout,
  SecondaryTopNav,
  PrimaryButton,
  Container,
  Stack,
  Grid,
  Text,
  showToast,
  Loader,
} from "@certn/cascadia-react";
import { Formik } from "formik";
import { t } from "i18next";
import { useNavigate, useParams } from "react-router-dom";
import { object, string } from "yup";

import { HeaderCard } from "./HeaderCard";
import { ActionsDropdown } from "../ActionsDropdown";
import { VerificationDetails } from "../components/VerificationDetails";
import { VerificationType } from "../components/VerificationType";
import { VERIFICATION_TYPE } from "../constants";
import { useEmploymentVerificationTemplatesInPackages } from "../hooks";
import { Schemas } from "../../../types";
import { CustomInstructionsFormSection } from "../../templates/CustomInstructionsFormSection";
import { EMPLOYMENT_DEFAULT_INSTRUCTIONS } from "../../templates/customInstructionsConstants";
import { customInstructionsSchemaField } from "../../templates/customInstructionsValidation";

export function VerificationTemplate() {
  const navigate = useNavigate();
  const { templateId } = useParams();

  const schema = object().shape({
    name: string().required().label(t("Template name")),
    custom_instructions: customInstructionsSchemaField(),
  });

  const templateIdsBelongingToActivePackages =
    useEmploymentVerificationTemplatesInPackages();

  const { data, mutate } = useClientPortalAPI(
    `/api/internal/client-portal/check-arguments-templates/employment-verification/${templateId}/`
  );

  const { data: allTemplates } = useClientPortalAPI(
    `/api/internal/client-portal/check-arguments-templates/employment-verification/`
  );

  const activeTemplates = allTemplates?.results.filter(
    (template) => template.is_active === true
  );

  const { submit } = useSubmit(
    `/api/internal/client-portal/check-arguments-templates/employment-verification/${templateId}/update/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: async () => {
        await mutate();
        showToast(t("Verification template has been updated."), {
          type: "success",
        });
        navigate("/settings/employment-verification-templates");
      },
    }
  );

  const handleExitForm = () => {
    navigate("/settings/employment-verification-templates");
  };

  if (!data) {
    return <Loader />;
  }

  const checkArguments =
    data.check_arguments as Schemas["CheckArgumentsCreate"];
  const isQuantity =
    data?.check_arguments?.validation_type === VERIFICATION_TYPE.quantity;

  const initialValues = {
    ...data,
    check_arguments: {
      ...data.check_arguments,
      fca_regulated_references_enabled: Boolean(
        data.check_arguments?.fca_firm_reference_number
      ),
    },
    custom_instructions:
      (data.check_arguments as { custom_instructions?: string } | null)
        ?.custom_instructions ?? "",
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values) =>
        await submit({
          name: values.name,
          custom_instructions: values.custom_instructions || "",
        })
      }
    >
      {({ handleSubmit, isSubmitting, isValid, dirty }) => {
        return (
          <Layout
            layout="narrow"
            header={
              <SecondaryTopNav
                testID="close-button"
                leftIcon="close"
                onleftIconOnPress={handleExitForm}
              >
                <PrimaryButton
                  onPress={() => handleSubmit()}
                  isLoading={isSubmitting}
                  isDisabled={!isValid || !dirty}
                >
                  {t("Save changes")}
                </PrimaryButton>
              </SecondaryTopNav>
            }
          >
            <Container>
              <Stack gap="space-3">
                <Text as="h1" style="global/title-medium">
                  {t("Employment Verification details")}
                </Text>
              </Stack>
              <Grid form>
                <HeaderCard
                  name={data.name}
                  trailing={
                    <ActionsDropdown
                      templateIdsBelongingToActivePackages={
                        templateIdsBelongingToActivePackages
                      }
                      templateId={data.id}
                      name={data.name}
                      isActive={data.is_active}
                      numActiveTemplates={activeTemplates?.length || 0}
                      mutate={mutate}
                    />
                  }
                />
                <VerificationType
                  isReadOnly
                  digital_verification={
                    checkArguments?.digital_verification ?? false
                  }
                />
                <VerificationDetails
                  isQuantity={isQuantity}
                  isReadOnly
                  checkArguments={checkArguments}
                />
                <CustomInstructionsFormSection
                  fieldName="custom_instructions"
                  defaultInstructionsPlaceholder={EMPLOYMENT_DEFAULT_INSTRUCTIONS()}
                />
              </Grid>
            </Container>
          </Layout>
        );
      }}
    </Formik>
  );
}
